import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import { distribuicaoLigada } from '@/leads/rodizio';
import { minutosDeReserva } from '@/conversas/reserva';

/**
 * Ajustes de atendimento da clínica.
 *
 * A assinatura das mensagens e — ★ WA — a distribuição de leads por rodízio (interruptor geral da clínica)
 * com os minutos da reserva da conversa.
 *
 * Antes da WA, só a assinatura. O BACKEND JÁ FAZ o trabalho: o
 * `enviarMensagem` prefixa `*Nome:*` quando `config.assinarMensagens` é true —
 * esta seção só liga e desliga o interruptor que já existia e ninguém tinha
 * como alcançar.
 */
export default function SecaoAtendimento() {
  const { clinicaId } = useAuth();
  const [clinica, setClinica] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(
      clinicaId,
      (doc) => {
        setClinica(doc);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ler a configuração da clínica:', e);
        setErro('Não foi possível carregar os ajustes da clínica.');
        setCarregando(false);
      },
    );
  }, [clinicaId]);

  // Ausente conta como desligado: é o mesmo que o servidor entende.
  const assinar = clinica?.config?.assinarMensagens === true;
  // ★ WA — distribuição de leads por rodízio: só 'rodizio' liga; o resto (inclusive ausente) é desligado.
  const distribuindo = distribuicaoLigada(clinica?.config);
  const minutosSalvos = minutosDeReserva(clinica?.config);
  const [minutos, setMinutos] = useState('');
  // O campo segue o banco enquanto ninguém o edita; editado, o que a pessoa digitou manda.
  const valorMinutos = minutos === '' ? String(minutosSalvos) : minutos;

  /** Um campo por vez, pelo caminho pontilhado — o mesmo cuidado do `alternar`. */
  async function salvarCampo(campo, valor) {
    setErro(null);
    setSalvando(true);
    try {
      await atualizarCampoConfig(clinicaId, campo, valor);
    } catch (e) {
      console.error(`[OdontoSDR] Falha ao salvar ${campo}:`, e);
      setErro(
        e.code === 'permission-denied'
          ? 'Apenas o gestor da clínica pode mudar este ajuste.'
          : 'Não foi possível salvar. Tente de novo.',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function alternar(valor) {
    setErro(null);
    setSalvando(true);
    try {
      // Campo único, por caminho pontilhado: gravar `config` inteiro apagaria o
      // funil customizado e o horário de atendimento que moram no mesmo objeto.
      await atualizarCampoConfig(clinicaId, 'assinarMensagens', valor);
      // A tela reflete sozinha — `ouvirClinica` é listener.
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar a assinatura:', e);
      setErro(
        e.code === 'permission-denied'
          ? 'Apenas o gestor da clínica pode mudar este ajuste.'
          : 'Não foi possível salvar. Tente de novo.',
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Atendimento</h2>
          <p className="secao-sub">Como as mensagens saem para o paciente.</p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="cartao">
        <label className="linha-ajuste">
          <input
            type="checkbox"
            checked={assinar}
            disabled={carregando || salvando}
            onChange={(e) => alternar(e.target.checked)}
          />
          <span>
            <strong>Assinar mensagens com o nome do atendente</strong>
            <span className="campo-ajuda">
              A mensagem sai com o nome de quem respondeu em negrito, antes do texto — assim o
              paciente sabe com quem está falando. Vale só para o que a equipe digita; saudação
              automática e mensagem de encerramento não são assinadas.
            </span>
          </span>
        </label>
      </div>

      {/* ★ WA — DISTRIBUIÇÃO DE LEADS (decisão do dono, 22/set/2026).
          O interruptor mostra o estado REAL do banco: estado implícito mente. Desligado, tudo volta a ser
          como antes — o lead nasce sem SDR e a conversa vai para a fila de todos. */}
      <div className="cartao">
        <label className="linha-ajuste">
          <input
            id="at-distribuicao"
            type="checkbox"
            checked={distribuindo}
            disabled={carregando || salvando}
            onChange={(e) => salvarCampo('distribuicaoLeads', e.target.checked ? 'rodizio' : 'manual')}
          />
          <span>
            <strong>Distribuir os leads novos por rodízio</strong>
            <span className="campo-ajuda">
              Cada lead que chega pelo WhatsApp já nasce com uma SDR responsável, um a um, em ordem. Quem
              entra no rodízio você marca na tela <strong>Equipe</strong> — ninguém entra sozinho. Desligado,
              o lead nasce sem SDR e quem atender primeiro assume, como antes.
            </span>
          </span>
        </label>

        <div className="linha-ajuste">
          <label htmlFor="at-reserva-min">
            <strong>Reserva da conversa</strong>
            <span className="campo-ajuda">
              Por quantos minutos a conversa fica guardada para a SDR do lead. Nesse tempo ela aparece em
              Aguardando para todo mundo, mas só ela pode atender (o gestor sempre pode). Passado o prazo,
              qualquer uma pode puxar — e leva o lead junto.
            </span>
          </label>
          <span className="linha-campo-curto">
            <input
              id="at-reserva-min"
              type="number"
              min="1"
              max="120"
              value={valorMinutos}
              disabled={carregando || salvando}
              onChange={(e) => setMinutos(e.target.value)}
            />
            <span className="campo-sufixo">min</span>
            <button
              id="at-reserva-salvar"
              type="button"
              className="btn-secundario compacto"
              disabled={carregando || salvando || Number(valorMinutos) === minutosSalvos || !(Number(valorMinutos) > 0)}
              onClick={async () => { await salvarCampo('reservaMinutos', Math.trunc(Number(valorMinutos))); setMinutos(''); }}
            >
              Salvar
            </button>
          </span>
        </div>
      </div>
    </section>
  );
}
